import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";
import { useBuchungsarten } from "../lib/receipts";
import { VOLL_BP } from "../../lib/rechnung.ts";

/**
 * Einstellungen › Verträge (User 18.10.2026).
 * Rechnungen eines Vertrags (z. B. Telefon) werden über die Kennung im Belegtext erkannt und nach den Anteilen vorbelegt — als Vorschlag,
 * bestätigt wird beim Prüfen. Kennungen sind keine Geheimnisse und stehen hier vollständig. Änderungen wirken für neue Belege und beim
 * Neu-Analysieren; vorhandene Aufteilungen bleiben. Neue Prozente ab einem Datum: alten Vertrag mit „gültig bis“ beenden, neuen anlegen.
 */
type Teil = { steuerArtId: string; anteilBp: number };
type Vertrag = { id: string; anbieter: string; kennung: string; gueltigAb: string | null; gueltigBis: string | null; aktiv: boolean; teile: Teil[] };
type Entwurf = Omit<Vertrag, "id">;

const LEER: Entwurf = { anbieter: "", kennung: "", gueltigAb: null, gueltigBis: null, aktiv: true, teile: [{ steuerArtId: "", anteilBp: 5000 }, { steuerArtId: "", anteilBp: 5000 }] };
const feldBasis = "border border-ios-line rounded-lg px-3 py-2 text-[16px] lg:text-[14px]";
const feld = `${feldBasis} w-full`;

function fehlertext(e: unknown) {
  return e instanceof ApiError ? e.message : "Speichern fehlgeschlagen";
}

export function VertraegePage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Vertrag[]>({ queryKey: ["vertraege"], queryFn: () => api.get("/api/vertraege") });
  const [neu, setNeu] = useState(false);
  const fertig = () => void qc.invalidateQueries({ queryKey: ["vertraege"] });

  return (
    <div className="max-w-2xl mx-auto p-4 lg:p-6">
      <Link to="/einstellungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3">
        <ChevronLeftIcon className="w-4 h-4" /> Einstellungen
      </Link>
      <h1 className="text-[22px] font-semibold tracking-tight mb-1">Verträge</h1>
      <p className="text-[13px] text-ios-muted mb-4">
        Rechnungen eines Vertrags werden an der Kennung (Kunden- oder Buchungskontonummer) erkannt und nach diesen Anteilen aufgeteilt — als
        Vorschlag, den du beim Prüfen bestätigst. Änderungen wirken für neue Belege; bereits aufgeteilte Belege bleiben, wie sie sind.
        Neue Prozente ab einem Datum: beim bisherigen Vertrag „gültig bis“ setzen und einen neuen anlegen.
      </p>
      {isLoading ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <div className="space-y-3">
          {(data ?? []).map((v) => <VertragKarte key={v.id} v={v} onFertig={fertig} />)}
          {(data ?? []).length === 0 && !neu && <p className="text-[14px] text-ios-muted">Noch keine Verträge.</p>}
          {neu ? <VertragKarte v={null} onFertig={() => { setNeu(false); fertig(); }} onAbbrechen={() => setNeu(false)} /> : (
            <button type="button" onClick={() => setNeu(true)} className="inline-flex items-center gap-1 bg-ios-blue text-white rounded-lg px-3 py-2 text-[14px]">
              <PlusIcon className="w-4 h-4" /> Neuer Vertrag
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function VertragKarte({ v, onFertig, onAbbrechen }: { v: Vertrag | null; onFertig: () => void; onAbbrechen?: () => void }) {
  const toast = useToast();
  const { data: artDaten } = useBuchungsarten("standard");
  const arten = artDaten?.arten ?? [];
  const [e, setE] = useState<Entwurf>(v ? { ...v, teile: v.teile.map((t) => ({ ...t })) } : LEER);
  const speichern = useMutation({
    mutationFn: (x: Entwurf) => (v ? api.put(`/api/vertraege/${v.id}`, x) : api.post("/api/vertraege", x)),
    onSuccess: (r: unknown) => {
      // Modell B: wie viele bestehende Buchungen/Belege ab 2025 neu aufgeteilt wurden (bestätigte stehen danach auf „geändert“)
      const n = (r as { neuAngewendet?: { buchungen: number; belege: number } } | null)?.neuAngewendet;
      toast.success(n && (n.buchungen || n.belege) ? `Vertrag gespeichert — neu aufgeteilt: ${n.buchungen} Buchungen, ${n.belege} Belege (bitte neu bestätigen)` : "Vertrag gespeichert");
      onFertig();
    },
    onError: (err) => toast.error(fehlertext(err)),
  });
  const loeschen = useMutation({
    mutationFn: () => api.del(`/api/vertraege/${v!.id}`),
    onSuccess: onFertig, onError: (err) => toast.error(fehlertext(err)),
  });
  const summe = e.teile.reduce((s, t) => s + (Number.isFinite(t.anteilBp) ? t.anteilBp : 0), 0);
  const setTeil = (i: number, t: Partial<Teil>) => setE({ ...e, teile: e.teile.map((x, k) => (k === i ? { ...x, ...t } : x)) });

  return (
    <form className="bg-white rounded-iosLg shadow-card p-4 space-y-3" onSubmit={(ev) => { ev.preventDefault(); speichern.mutate(e); }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-[13px] text-ios-muted">Anbieter
          <input className={feld} value={e.anbieter} maxLength={60} onChange={(x) => setE({ ...e, anbieter: x.target.value })} placeholder="z. B. Telekom Festnetz" />
        </label>
        <label className="text-[13px] text-ios-muted">Kennung (Kunden-/Buchungskontonummer)
          <input className={`${feld} font-mono`} value={e.kennung} inputMode="numeric" onChange={(x) => setE({ ...e, kennung: x.target.value })} />
        </label>
        <label className="text-[13px] text-ios-muted">gültig ab (leer = seit jeher)
          <input type="date" className={feld} value={e.gueltigAb ?? ""} onChange={(x) => setE({ ...e, gueltigAb: x.target.value || null })} />
        </label>
        <label className="text-[13px] text-ios-muted">gültig bis (leer = offen)
          <input type="date" className={feld} value={e.gueltigBis ?? ""} onChange={(x) => setE({ ...e, gueltigBis: x.target.value || null })} />
        </label>
      </div>
      <div>
        <div className="text-[13px] text-ios-muted mb-1">Aufteilung</div>
        {e.teile.map((t, i) => (
          <div key={i} className="flex gap-2 mb-2 items-center">
            <select className={`${feldBasis} flex-1 min-w-0`} value={t.steuerArtId} onChange={(x) => setTeil(i, { steuerArtId: x.target.value })}>
              <option value="">— Buchungsart wählen —</option>
              {arten.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input className={`${feldBasis} w-20 shrink-0 text-right`} inputMode="decimal" value={String(t.anteilBp / 100).replace(".", ",")}
              onChange={(x) => setTeil(i, { anteilBp: Math.round(Number(x.target.value.replace(",", ".")) * 100) })} />
            <span className="text-[14px]">%</span>
            <button type="button" aria-label="Teil entfernen" disabled={e.teile.length <= 2} className="text-ios-subtle disabled:opacity-30"
              onClick={() => setE({ ...e, teile: e.teile.filter((_, k) => k !== i) })}><TrashIcon className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-3 text-[13px]">
          <button type="button" className="text-ios-blue" onClick={() => setE({ ...e, teile: [...e.teile, { steuerArtId: "", anteilBp: 0 }] })}>+ Teil</button>
          <span className={summe === VOLL_BP ? "text-ios-green" : "text-ios-blue"}>Summe {String(summe / 100).replace(".", ",")} %{summe === VOLL_BP ? " ✓" : " — muss 100 % sein"}</span>
        </div>
      </div>
      <label className="flex items-center gap-2 text-[14px]">
        <input type="checkbox" checked={e.aktiv} onChange={(x) => setE({ ...e, aktiv: x.target.checked })} /> aktiv
      </label>
      <div className="flex gap-2 justify-end">
        {v && <button type="button" className="text-[14px] text-ios-subtle mr-auto" onClick={() => loeschen.mutate()}>Löschen</button>}
        {onAbbrechen && <button type="button" className="text-[14px] px-3 py-1.5 rounded-ios bg-ios-line/60" onClick={onAbbrechen}>Abbrechen</button>}
        <button type="submit" disabled={speichern.isPending} className="text-[14px] px-3 py-1.5 rounded-ios bg-ios-blue text-white disabled:opacity-50">Speichern</button>
      </div>
    </form>
  );
}
